import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';

const OffersTable = ({ offers, contract, signer, fetchOffers, account }) => {
  const [liquidTokenAddress, setLiquidTokenAddress] = useState('0x0000000000000000000000000000000000000000');
  const [statusMessage, setStatusMessage] = useState('');

  useEffect(() => {
    const checkLiquidToken = async () => {
      if (contract) {
        try {
          const address = await contract.liquidToken();
          setLiquidTokenAddress(address);
        } catch (error) {
          console.error('Error fetching liquidToken:', error);
        }
      }
    };

    checkLiquidToken();
    const interval = setInterval(checkLiquidToken, 30000); // Check every 30 seconds

    return () => clearInterval(interval);
  }, [contract]);

  const handleAcceptOffer = async (offerIndex, sellerCollateral) => {
    try {
      if (!contract) {
        setStatusMessage('Please connect your wallet first.');
        return;
      }

      setStatusMessage('Accepting offer...');
      const tx = await contract.acceptOffer(offerIndex, {
        value: sellerCollateral
      });
      
      setStatusMessage('Transaction sent. Waiting for confirmation...');
      await tx.wait();
      
      setStatusMessage('Offer accepted successfully!');
      fetchOffers();
    } catch (error) {
      console.error('Error accepting offer:', error);
      setStatusMessage('Error accepting offer. Please try again.');
    }
  };

  const handleCancelOffer = async (offerIndex) => {
    try {
      if (!contract) {
        setStatusMessage('Please connect your wallet first.');
        return;
      }

      setStatusMessage('Cancelling offer...');
      const tx = await contract.cancelOffer(offerIndex);
      
      setStatusMessage('Transaction sent. Waiting for confirmation...');
      await tx.wait();
      
      setStatusMessage('Offer cancelled successfully!');
      fetchOffers();
    } catch (error) {
      console.error('Error cancelling offer:', error);
      setStatusMessage('Error cancelling offer. Please try again.');
    }
  };

  const handleSettle = async (offerIndex, points) => {
    try {
      if (!contract) {
        setStatusMessage('Please connect your wallet first.');
        return;
      }

      setStatusMessage('Approving token transfer...');
      const tokenContract = new ethers.Contract(liquidTokenAddress, [
        "function approve(address spender, uint256 amount) public returns (bool)"
      ], signer);

      const approveTx = await tokenContract.approve(contract.address, points);
      await approveTx.wait();

      setStatusMessage('Settling offer...');
      const gasEstimate = await contract.estimateGas.settle(offerIndex);
      const gasLimit = gasEstimate.mul(120).div(100);

      const settleTx = await contract.settle(offerIndex, { gasLimit });
      setStatusMessage('Transaction sent. Waiting for confirmation...');
      await settleTx.wait();

      setStatusMessage('Offer settled successfully!');
      fetchOffers();
    } catch (error) {
      console.error('Error settling offer:', error);
      if (error.message.includes("execution reverted")) {
        const reason = error.data?.message || "Unknown reason";
        setStatusMessage(`Error settling offer: ${reason}`);
      } else {
        setStatusMessage('Error settling offer. Please try again.');
      }
    }
  };

  const handleClaim = async (offerIndex) => {
    try {
      if (!contract) {
        setStatusMessage('Please connect your wallet first.');
        return;
      }

      setStatusMessage('Claiming tokens...');
      const tx = await contract.claimTokens(offerIndex);
      
      setStatusMessage('Transaction sent. Waiting for confirmation...');
      await tx.wait();
      
      setStatusMessage('Tokens claimed successfully!');
      fetchOffers();
    } catch (error) {
      console.error('Error claiming tokens:', error);
      setStatusMessage('Error claiming tokens. Please try again.');
    }
  };

  const formatMarks = (marks) => {
    const formattedMarks = ethers.utils.formatUnits(marks, 18);
    const [wholePart, decimalPart] = formattedMarks.split('.');
    if (wholePart.length >= 4) {
      return wholePart.slice(0, 4);
    } else {
      return (wholePart + '.' + decimalPart).slice(0, 4);
    }
  };

  const openOffers = offers.filter(offer => offer.status === 0);
  const yourBuys = offers.filter(offer => 
    offer.status === 1 && offer.buyer.toLowerCase() === account.toLowerCase()
  );
  const yourSales = offers.filter(offer => 
    offer.status === 1 && offer.seller.toLowerCase() === account.toLowerCase()
  );
  const yourSettledAndClaimed = offers.filter(offer => 
    (offer.status === 2 || offer.status === 3) && 
    (offer.seller.toLowerCase() === account.toLowerCase() || offer.buyer.toLowerCase() === account.toLowerCase())
  );

  const renderCompactTable = (title, data) => (
    <div className={`card ${title.toLowerCase().replace(' & ', '-').replace(' ', '-')}`}>
      <h2>{title}</h2>
      <table className="compact-table">
        <tbody>
          {data.map((offer, index) => (
            <React.Fragment key={index}>
              <tr className="offer-row">
                <td rowSpan="2" className="marks-cell">
                  <strong>Marks:</strong><br />
                  {formatMarks(offer.points)}
                </td>
                <td>
                  <strong>Seller:</strong> {offer.seller.slice(0, 6)}...{offer.seller.slice(-4)}<br />
                  <strong>Collateral:</strong> {ethers.utils.formatEther(offer.sellerCollateral)} ETH
                </td>
                <td rowSpan="2" className="status-cell">
                  <strong>Status:</strong><br />
                  {['OPEN', 'ACCEPTED', 'SETTLED', 'CLAIMED', 'CANCELLED'][offer.status]}
                  {liquidTokenAddress !== '0x0000000000000000000000000000000000000000' &&
                   offer.status === 1 &&
                   title === "Your Sales" && (
                    <button 
                      className="settle-offer"
                      onClick={() => handleSettle(offer.index, offer.points)}
                    >
                      Settle
                    </button>
                  )}
                  {offer.status === 2 &&
                   offer.buyer.toLowerCase() === account.toLowerCase() &&
                   title === "Settled & Claimed" && (
                    <button 
                      className="claim-tokens"
                      onClick={() => handleClaim(offer.index)}
                    >
                      Claim
                    </button>
                  )}
                </td>
              </tr>
              <tr className="offer-row">
                <td>
                  <strong>Buyer:</strong> {offer.buyer.slice(0, 6)}...{offer.buyer.slice(-4)}<br />
                  <strong>Collateral:</strong> {ethers.utils.formatEther(offer.buyerCollateral)} ETH
                </td>
              </tr>
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderTable = (title, data, showAction = false, isCurrentOffers = false) => (
    <div className={`card ${title.toLowerCase().replace(' ', '-')}`}>
      <h2>{title}</h2>
      <table>
        <thead>
          <tr>
            <th>Marks</th>
            <th>Seller</th>
            <th>{isCurrentOffers ? "Purchase Price" : "Seller Collateral"}</th>
            {!isCurrentOffers && (
              <>
                <th>Buyer</th>
                <th>Buyer Collateral</th>
              </>
            )}
            <th>Status</th>
            {showAction && <th>Action</th>}
          </tr>
        </thead>
        <tbody>
          {data.map((offer, index) => (
            <tr key={index}>
              <td>{formatMarks(offer.points)}</td>
              <td>{offer.seller}</td>
              <td>{ethers.utils.formatEther(offer.sellerCollateral)} ETH</td>
              {!isCurrentOffers && (
                <>
                  <td>{offer.buyer}</td>
                  <td>{ethers.utils.formatEther(offer.buyerCollateral)} ETH</td>
                </>
              )}
              <td>{['OPEN', 'ACCEPTED', 'SETTLED', 'CLAIMED', 'CANCELLED'][offer.status]}</td>
              {showAction && (
                <td>
                  {offer.seller.toLowerCase() !== account.toLowerCase() ? (
                    <button className="accept-offer" onClick={() => handleAcceptOffer(offer.index, offer.sellerCollateral)}>
                      Accept Offer
                    </button>
                  ) : (
                    offer.status === 0 && (
                      <button className="cancel-offer" onClick={() => handleCancelOffer(offer.index)}>
                        Cancel Offer
                      </button>
                    )
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="offers-container">
      {statusMessage && <div className="status-message">{statusMessage}</div>}
      {renderTable("Open Offers", openOffers, true, true)}
      {account && (
        <>
          {renderCompactTable("Your Buys", yourBuys)}
          {renderCompactTable("Your Sales", yourSales)}
          {renderCompactTable("Settled & Claimed", yourSettledAndClaimed)}
        </>
      )}
    </div>
  );
};

export default OffersTable;